import type { NavItem } from '#engage-market-maker/utils/engage-a-market-maker'
import type { RFQSurveySchemaType } from '@forgd/contract/types'
import type { RfqRound } from '@forgd/shared/rfq/types'
import { getNavItems } from '#engage-market-maker/utils/engage-a-market-maker'
import { FORGD_SYSTEM_PROJECT_ID } from '@forgd/shared/constants'
import { MarketMakerRequestStatusEnum } from '@forgd/shared/enums'
import { MAX_DAYS_FOR_USER_TO_MAKE_DECISION_ON_QUOTES, MAX_SELECTED_QUOTES } from '@forgd/shared/rfq/constants'
import { useMutation, useQuery } from '@pinia/colada'
import { addDays, parse } from 'date-fns'
import { defineStore } from 'pinia'

interface RoundInfo {
  type: 'accepted' | 'requoted' | 'new'
  dateISO: string
  round?: number
}

export const useRFQDetails = defineStore('engage-a-market-maker', () => {
  const auth = useAuth()
  const isDemoMode = useRfqDemoMode()

  const { mutateAsync: requoteAsync } = useMutation(buildApiMutation('/rfq/project/requote/{projectId}', {
    method: 'post',
  }))

  const { mutateAsync: acceptAsync } = useMutation(buildApiMutation('/rfq/project/accept/{projectId}', {
    method: 'post',
  }))
  const toast = useAppToast()

  const projectId = computed(() =>
    auth.project?.isResearch ? FORGD_SYSTEM_PROJECT_ID : auth.project?.id,
  )
  const engageMarketMakerBaseUrl = '/engage-a-market-maker'
  const loadingAction = ref<boolean>(false)
  const currentRound = ref<number>(1)
  const navItems = computed<NavItem[]>(() => getNavItems(engageMarketMakerBaseUrl, data.value?.statuses, data.value?.rfq?.round))

  /**
   * selected[Foo]Native = the format used by tanstack table, so we can control the selection
   */
  const selectedLoanCallOptionQuotesNative = ref<Record<string, boolean>>({})
  const selectedLoanCallOptionQuotes = computed(() => Object.entries(selectedLoanCallOptionQuotesNative.value).filter(([_, value]) => value).map(([key]) => key))
  const selectedRetainerWorkingCapitalQuotesNative = ref<Record<string, boolean>>({})
  const selectedRetainerWorkingCapitalQuotes = computed(() => Object.entries(selectedRetainerWorkingCapitalQuotesNative.value).filter(([_, value]) => value).map(([key]) => key))
  const totalSelectedQuotes = computed(() => selectedLoanCallOptionQuotes.value.length + selectedRetainerWorkingCapitalQuotes.value.length)
  const showMaxLimitModal = ref(false)

  /**
   * Same market maker conflict modal state
   */
  const showSameMmModal = ref(false)
  const sameMmConflictQuote = ref<{ id: string, marketMakerId: string, marketMakerName: string, engagementType: string } | null>(null)

  /**
   * Full quote objects for selected quotes (used by QuoteSelectionWidget)
   */
  const selectedLoanCallOptionQuoteObjects = computed(() =>
    dataRoundQuotes.value?.quotes.loan_call_option.filter(q => selectedLoanCallOptionQuotes.value.includes(q.id)) ?? [],
  )
  const selectedRetainerWorkingCapitalQuoteObjects = computed(() =>
    dataRoundQuotes.value?.quotes.retainer_working_capital.filter(q => selectedRetainerWorkingCapitalQuotes.value.includes(q.id)) ?? [],
  )

  /**
   * Set by select button in MmQuoteReviewTable.vue
   */
  const lastSelectedQuoteId = ref<string | null>(null)

  /**
   * The public demo snapshot mirrors the shapes of the live reads below. On demo
   * routes the live queries are disabled and the `data*` accessors switch source,
   * so consumers render demo data through the same code path.
   */
  const { data: demoSnapshot, isLoading: loadingDemo, refetch: fetchDemoAsync } = useQuery({
    ...buildApiQuery('/rfq/demo'),
    enabled: () => isDemoMode.value,
    refetchOnWindowFocus: false,
  })

  const { data: liveDetails, isLoading: loadingLiveDetails, refetch: fetchLiveAsync } = useQuery({
    ...buildApiQuery('/rfq/project/details/{projectId}', {
      params: () => ({
        path: {
          projectId: String(projectId.value),
        },
      }),
    }),
    enabled: () => !isDemoMode.value && Boolean(projectId.value),
    refetchOnWindowFocus: false,
  })
  const { isLoading: loadingRound, data: liveRound, refetch: fetchRoundAsync } = useQuery({
    ...buildApiQuery('/rfq/project/round/{projectId}', {
      params: () => ({
        path: {
          projectId: String(projectId.value),
        },
        query: {
          round: currentRound.value,
        },
      }),
    }),
    enabled: () => !isDemoMode.value && Boolean(projectId.value),
    refetchOnWindowFocus: false,
  })
  const { isLoading: loadingLiveRoundQuotes, data: liveRoundQuotes, refetch: fetchRoundQuotesAsync } = useQuery({
    ...buildApiQuery('/rfq/project/round/{projectId}/quotes', {
      params: () => ({
        path: {
          projectId: String(projectId.value),
        },
        query: {
          round: currentRound.value,
        },
      }),
    }),
    enabled: () => !isDemoMode.value && Boolean(projectId.value),
    refetchOnWindowFocus: false,
  })
  const { isLoading: loadingLiveRoundSelection, data: liveRoundSelection, refetch: fetchRoundSelectionAsync } = useQuery({
    ...buildApiQuery('/rfq/project/round/{projectId}/quotes/selection', {
      params: () => ({
        path: {
          projectId: String(projectId.value),
        },
        query: {
          round: currentRound.value,
          selectedQuoteIds: totalSelectedQuotes.value > 0 ? [...selectedLoanCallOptionQuotes.value, ...selectedRetainerWorkingCapitalQuotes.value] : undefined,
        },
      }),
    }),
    enabled: () => !isDemoMode.value && Boolean(projectId.value),
    refetchOnWindowFocus: false,
  })

  const data = computed(() => (isDemoMode.value ? demoSnapshot.value?.snapshot?.details : liveDetails.value))
  const dataRound = computed(() => (isDemoMode.value ? demoSnapshot.value?.snapshot?.round : liveRound.value))
  const dataRoundQuotes = computed(() => (isDemoMode.value ? demoSnapshot.value?.snapshot?.quotes : liveRoundQuotes.value))
  // Selection preview is only used by the Preview & Submission step, which the demo can't reach.
  const dataRoundSelection = computed(() => (isDemoMode.value ? undefined : liveRoundSelection.value))
  const loadingRoundQuotes = computed(() => (isDemoMode.value ? loadingDemo.value : loadingLiveRoundQuotes.value))
  const loadingRoundSelection = computed(() => (isDemoMode.value ? false : loadingLiveRoundSelection.value))

  const isDemoUnavailable = computed(() =>
    isDemoMode.value && demoSnapshot.value !== undefined && demoSnapshot.value.snapshot === null,
  )

  const demoQuoteSummaries = computed(() =>
    isDemoMode.value ? demoSnapshot.value?.snapshot?.quoteSummaries : undefined,
  )

  const demoMarketMakerProfiles = computed(() =>
    isDemoMode.value ? demoSnapshot.value?.snapshot?.marketMakerProfiles : undefined,
  )

  async function fetch() {
    return isDemoMode.value ? await fetchDemoAsync() : await fetchLiveAsync()
  }

  const maxSelectedQuotes = computed(() => data.value?.rfq?.maxSelectedQuotes ?? MAX_SELECTED_QUOTES)

  watch(() => totalSelectedQuotes.value, () => {
    if (totalSelectedQuotes.value > maxSelectedQuotes.value) {
      showMaxLimitModal.value = true
      // remove the last selected quote id from the native selection
      if (lastSelectedQuoteId.value) {
        selectedLoanCallOptionQuotesNative.value[lastSelectedQuoteId.value] = false
        selectedRetainerWorkingCapitalQuotesNative.value[lastSelectedQuoteId.value] = false
      }
    }
  })

  // Clean up stale selections when quotes are loaded (persisted IDs that no longer exist)
  watch(() => dataRoundQuotes.value, (newQuotes) => {
    if (!newQuotes) {
      return
    }

    const loanCallIds = new Set(newQuotes.quotes.loan_call_option.map(q => q.id))
    const retainerIds = new Set(newQuotes.quotes.retainer_working_capital.map(q => q.id))

    // Remove any selected loan call option IDs that don't exist in current quotes
    for (const id of Object.keys(selectedLoanCallOptionQuotesNative.value)) {
      if (!loanCallIds.has(id)) {
        delete selectedLoanCallOptionQuotesNative.value[id]
      }
    }

    // Remove any selected retainer working capital IDs that don't exist in current quotes
    for (const id of Object.keys(selectedRetainerWorkingCapitalQuotesNative.value)) {
      if (!retainerIds.has(id)) {
        delete selectedRetainerWorkingCapitalQuotesNative.value[id]
      }
    }
  }, { immediate: true })

  async function fetchRound(round: number) {
    if (isDemoMode.value) {
      return
    }
    currentRound.value = round
    await fetchRoundAsync()
  }

  async function fetchRoundSelection(round: number) {
    if (isDemoMode.value) {
      return
    }
    currentRound.value = round
    await fetchRoundSelectionAsync()
  }

  async function fetchRoundQuotes(round: number) {
    if (isDemoMode.value) {
      return
    }
    currentRound.value = round
    await fetchRoundQuotesAsync()
  }

  async function accept(round: RfqRound, survey?: RFQSurveySchemaType) {
    if (isDemoMode.value) {
      return
    }
    try {
      loadingAction.value = true
      await acceptAsync({
        path: {
          projectId: String(projectId.value),
        },
        body: {
          round,
          quoteIds: [...selectedLoanCallOptionQuotes.value, ...selectedRetainerWorkingCapitalQuotes.value],
          survey,
        },
      })
    }
    catch {
      toast.error({
        title: 'Something went wrong',
        description: 'Please try again.',
      })
    }
    finally {
      loadingAction.value = false
    }
  }

  async function requote(survey?: RFQSurveySchemaType) {
    if (isDemoMode.value) {
      return
    }
    try {
      loadingAction.value = true
      await requoteAsync({
        path: {
          projectId: String(projectId.value),
        },
        body: {
          quoteIds: [...selectedLoanCallOptionQuotes.value, ...selectedRetainerWorkingCapitalQuotes.value],
          survey,
        },
      })
    }
    catch {
      toast.error({
        title: 'Something went wrong',
        description: 'Please try again.',
      })
    }
    finally {
      loadingAction.value = false
    }
  }

  function resetSelection() {
    selectedLoanCallOptionQuotesNative.value = {}
    selectedRetainerWorkingCapitalQuotesNative.value = {}
  }

  /**
   * Swap selection: deselect any quote from the same MM and select the new one
   */
  function swapQuoteSelection() {
    if (!sameMmConflictQuote.value) {
      return
    }

    const { id, marketMakerId, engagementType } = sameMmConflictQuote.value
    const allQuotes = [
      ...(dataRoundQuotes.value?.quotes.loan_call_option ?? []),
      ...(dataRoundQuotes.value?.quotes.retainer_working_capital ?? []),
    ]

    // Find and deselect the currently selected quote from the same MM
    const selectedIds = [...selectedLoanCallOptionQuotes.value, ...selectedRetainerWorkingCapitalQuotes.value]
    const conflictingQuote = allQuotes.find(
      q => q.marketMakerId === marketMakerId && selectedIds.includes(q.id),
    )

    if (conflictingQuote) {
      if (conflictingQuote.engagementType === 'loan_call_option') {
        selectedLoanCallOptionQuotesNative.value[conflictingQuote.id] = false
      }
      else {
        selectedRetainerWorkingCapitalQuotesNative.value[conflictingQuote.id] = false
      }
    }

    // Select the new quote
    if (engagementType === 'loan_call_option') {
      selectedLoanCallOptionQuotesNative.value[id] = true
    }
    else {
      selectedRetainerWorkingCapitalQuotesNative.value[id] = true
    }

    // Close the modal and clear the conflict state
    showSameMmModal.value = false
    sameMmConflictQuote.value = null
  }

  function isFormDirty() {
    return Object.keys(selectedLoanCallOptionQuotesNative.value).length > 0 || Object.keys(selectedRetainerWorkingCapitalQuotesNative.value).length > 0
  }

  const roundDateInfo = computed<RoundInfo[]>(() => {
    const acceptedRound1Status = data?.value?.statuses.find(d => d.status === MarketMakerRequestStatusEnum.RoundOneQuotesAccepted)
    const acceptedRound2Status = data?.value?.statuses.find(d => d.status === MarketMakerRequestStatusEnum.RoundTwoQuotesAccepted)
    const requotedRound1Status = data?.value?.statuses.find(d => d.status === MarketMakerRequestStatusEnum.RoundTwoWaitingForQuotes)
    const info: RoundInfo[] = []

    if (acceptedRound1Status?.date || requotedRound1Status?.date) {
      info.push({
        type: acceptedRound1Status ? 'accepted' : 'requoted',
        dateISO: (acceptedRound1Status?.date || requotedRound1Status?.date)!,
        round: 1,
      })
    }

    if (acceptedRound2Status?.date) {
      info.push({
        type: 'accepted',
        dateISO: acceptedRound2Status.date,
        round: 2,
      })
    }

    if (dataRound?.value?.quotesSummary.quotesReceptionDate) {
      const date = parse(dataRound!.value.quotesSummary.quotesReceptionDate, 'yyyy-MM-dd', new Date())
      const dateToReply = addDays(date, MAX_DAYS_FOR_USER_TO_MAKE_DECISION_ON_QUOTES)
      info.push({
        type: 'new',
        dateISO: dateToReply.toISOString(),
      })
    }

    return info
  })

  watch(() => auth.project?.id, (newId, oldId) => {
    if (oldId && newId && newId !== oldId) {
      fetch()
    }
  })

  const isLoading = computed(() => {
    return loadingLiveDetails.value || loadingDemo.value || loadingRound.value || loadingAction.value || loadingRoundSelection.value
  })

  return {
    loading: isLoading,
    loadingRoundQuotes,
    loadingRoundSelection,
    isDemoUnavailable,
    demoQuoteSummaries,
    demoMarketMakerProfiles,
    data,
    dataRound,
    dataRoundQuotes,
    dataRoundSelection,
    engageMarketMakerBaseUrl,
    navItems,
    roundDateInfo,
    selectedLoanCallOptionQuotes,
    selectedLoanCallOptionQuotesNative,
    selectedLoanCallOptionQuoteObjects,
    selectedRetainerWorkingCapitalQuotes,
    selectedRetainerWorkingCapitalQuotesNative,
    selectedRetainerWorkingCapitalQuoteObjects,
    totalSelectedQuotes,
    maxSelectedQuotes,
    showMaxLimitModal,
    showSameMmModal,
    sameMmConflictQuote,
    lastSelectedQuoteId,
    isFormDirty,
    fetch,
    fetchRound,
    fetchRoundQuotes,
    fetchRoundSelection,
    accept,
    requote,
    resetSelection,
    swapQuoteSelection,
  }
}, {
  persist: {
    pick: ['selectedLoanCallOptionQuotesNative', 'selectedRetainerWorkingCapitalQuotesNative'],
  },
})
